import { Link } from "react-router-dom";
import { SignIn, SignUp, useClerk } from "@clerk/react";
import { ArrowRight, BookOpen, GraduationCap, Megaphone, ShieldQuestion } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO = "https://images.unsplash.com/photo-1758270704787-615782711641?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";

const AuthLayout = ({ children }) => (
  <div className="flex min-h-[100dvh] bg-background">
    <div className="relative hidden w-1/2 overflow-hidden lg:flex">
      <img src={HERO} alt="Studenti in classe" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-slate-950/10" />
      <div className="relative mt-auto p-12 text-white">
        <p className="font-heading text-4xl font-extrabold leading-tight">La tua classe,<br />tutta in un posto.</p>
      </div>
    </div>
    <div className="flex flex-1 items-center justify-center px-4 py-10 pt-[calc(2.5rem+env(safe-area-inset-top))]">{children}</div>
  </div>
);

export const SignInPage = () => (
  <AuthLayout><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthLayout>
);

export const SignUpPage = () => (
  <AuthLayout><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthLayout>
);

const FEATURES = [
  { icon: BookOpen, t: "Diario e compiti", d: "Argomenti del giorno e compiti per domani." },
  { icon: GraduationCap, t: "Verifiche", d: "Interrogazioni programmate e candidature." },
  { icon: Megaphone, t: "Bacheca", d: "Avvisi dei rappresentanti e circolari." },
];

export function Welcome() {
  return (
    <div className="min-h-[100dvh] bg-background pt-[env(safe-area-inset-top)]">
      <div className="relative h-64 overflow-hidden sm:h-80">
        <img src={HERO} alt="Studenti in classe" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
      </div>
      <div className="mx-auto -mt-16 max-w-xl px-5 pb-12">
        <img src="/logo-classe.png" alt="Logo Liceo Scientifico Filolao" className="h-20 w-20 rounded-full bg-white object-contain shadow-lg fade-up" data-testid="welcome-logo" />
        <h1 className="mt-5 font-heading text-4xl font-extrabold tracking-tight fade-up" style={{ animationDelay: "60ms" }} data-testid="welcome-title">Classe 1N</h1>
        <p className="mt-2 text-base text-muted-foreground fade-up" style={{ animationDelay: "120ms" }}>Il diario digitale e l'orario della tua classe, gestito dai rappresentanti.</p>
        <Button asChild size="lg" className="mt-7 h-12 w-full rounded-full text-base fade-up" style={{ animationDelay: "180ms" }} data-testid="welcome-login-btn">
          <Link to="/sign-in">Accedi <ArrowRight className="ml-1 h-4 w-4" /></Link>
        </Button>
        <p className="mt-3 text-center text-xs text-muted-foreground">L'account te lo crea il rappresentante di classe.</p>
        <div className="mt-10 space-y-3">
          {FEATURES.map((f, i) => (
            <div key={f.t} className="flex items-start gap-3 rounded-2xl border border-border/70 bg-card p-4 fade-up" style={{ animationDelay: `${240 + i * 60}ms` }}>
              <f.icon className="mt-0.5 h-5 w-5 text-primary" />
              <div><p className="font-semibold">{f.t}</p><p className="text-sm text-muted-foreground">{f.d}</p></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function NotEnrolled() {
  const { signOut, user } = useClerk();
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-5">
      <div className="max-w-sm text-center" data-testid="not-enrolled-screen">
        <ShieldQuestion className="mx-auto h-12 w-12 text-primary" />
        <h1 className="mt-4 font-heading text-2xl font-extrabold">Non sei ancora nella classe</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          L'email {user?.primaryEmailAddress?.emailAddress} non è stata aggiunta da un rappresentante. Chiedi di essere aggiunto, poi accedi di nuovo.
        </p>
        <Button className="mt-6 rounded-full" onClick={() => signOut()} data-testid="not-enrolled-signout-btn">Esci</Button>
      </div>
    </div>
  );
}
